import { formatAppDateTime } from "../../app/utils/appTimeFormat";

export const PREDICTION_TIMING_META = {
  upcoming: { label: "Upcoming", tone: "live" },
  live: { label: "Live", tone: "warn" },
  past: { label: "Past", tone: "muted" },
  unknown: { label: "—", tone: "muted" },
};

export function predictionTimingMeta(timing) {
  return (
    PREDICTION_TIMING_META[timing] || {
      label: timing || "—",
      tone: "muted",
    }
  );
}

/** Ledger/detail timing chip — only upcoming & live (never the word "Past"). */
export function predictionTimingBadge(timing) {
  if (timing === "upcoming" || timing === "live") {
    return predictionTimingMeta(timing);
  }
  return null;
}

export function predictionMatchLabel(home, away, fixtureStatus) {
  const homeName = home || null;
  const awayName = away || null;
  const status = fixtureStatus ? String(fixtureStatus).toUpperCase() : "—";
  if (homeName && awayName) return `${homeName} ${status} ${awayName}`;
  if (homeName || awayName) return [homeName, awayName].filter(Boolean).join(` ${status} `);
  return "Match";
}

/** Shape for P2P-style fixture matchup components (logos + status chip). */
export function predictionFixtureDuel(prediction) {
  return {
    matchHome: prediction?.matchHome || null,
    matchAway: prediction?.matchAway || null,
    homeLogo: prediction?.homeLogo || null,
    awayLogo: prediction?.awayLogo || null,
    fixtureStatus: prediction?.fixtureStatus || null,
    matchLabel: prediction?.matchLabel || null,
  };
}

export function predictionMatchInitials(home, away) {
  const h = String(home || "?").trim()[0] || "?";
  const a = String(away || "?").trim()[0] || "?";
  return (h + a).toUpperCase();
}

export function formatPredictionAt(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDateTime(d);
}

/** Compact ledger subline (author + tip + engagement). */
export function predictionLedgerSubline(prediction, formatCompact) {
  const parts = [];
  if (prediction.userName) parts.push(prediction.userName);

  const outcomes = Number(prediction.outcomeCount) || 0;
  if (outcomes > 1) parts.push(`${outcomes} outcomes`);
  else if (prediction.topOutcome) parts.push(prediction.topOutcome);

  if (prediction.avgConfidence != null) parts.push(`${prediction.avgConfidence}% conf`);

  const views = Number(prediction.views) || 0;
  if (views > 0) parts.push(`${views} views`);
  if (prediction.likesCount > 0) parts.push(`${prediction.likesCount} likes`);

  if (prediction.isPremium && prediction.price > 0) {
    parts.push(`${formatCompact(prediction.price)} premium`);
  }

  return parts.length ? parts.join(" · ") : "Community tip";
}

export function predictionTipLine(prediction) {
  const parts = [];
  const outcomes = Number(prediction.outcomeCount) || 0;
  if (outcomes > 1) parts.push(`${outcomes} outcomes`);
  else if (prediction.topOutcome) parts.push(prediction.topOutcome);
  if (prediction.avgConfidence != null) parts.push(`${prediction.avgConfidence}% conf`);
  return parts.length ? parts.join(" · ") : "—";
}

export function predictionContextLine(prediction) {
  const parts = [formatPredictionAt(prediction.createdAt), predictionTipLine(prediction)];
  return parts.filter((p) => p && p !== "—").join(" · ");
}

export function predictionMetricsLine(prediction, formatCompact) {
  const parts = [];

  if (prediction.views > 0) parts.push(`${prediction.views} views`);
  if (prediction.likesCount > 0) parts.push(`${prediction.likesCount} likes`);
  if (prediction.unlocksCount > 0) parts.push(`${prediction.unlocksCount} unlocks`);

  if (prediction.isPremium && prediction.price > 0) {
    parts.push(`${formatCompact(prediction.price)} premium`);
  }

  return parts.join(" · ") || "Community tip";
}

export function predictionSummaryParts(summary) {
  if (!summary) return [];
  const parts = [];
  if (summary.upcoming) parts.push(`${summary.upcoming} upcoming`);
  if (summary.past) parts.push(`${summary.past} past`);
  if (summary.premium) parts.push(`${summary.premium} premium`);
  return parts;
}

export function predictionDeskSummaryParts(summary) {
  if (!summary) return [];
  const parts = [];
  if (summary.upcoming) parts.push(`${summary.upcoming} upcoming`);
  if (summary.live) parts.push(`${summary.live} live`);
  if (summary.premium) parts.push(`${summary.premium} premium`);
  return parts;
}
